import { Actor, Canvas, Engine, Random, Vector, vec } from 'excalibur';
import { toRadians } from '../utils/angles';
import { DESIGN_WIDTH, SEEDS } from '../utils/constants';
import { Palette } from '../utils/palette';
import { Expression, FUNNY_EXPRESSIONS, drawFace, drawTaperedStroke, paintClockBase } from '../utils/sketch';

export interface MascotClockOptions {
  pos?: Vector;
  scale?: number;
}

export class MascotClock extends Actor {
  private readonly arm: Actor;
  private readonly bodyCanvas: Canvas;
  private readonly armCanvases: Canvas[] = [];
  private readonly faceRng = new Random(SEEDS.mascot + 11);
  private time = 0;
  private blinkTimer = 2.2;
  private blinking = false;
  private expression: Expression = 'idle';
  private expressionTimer = 0;
  private funnyTimer = 3.5;

  constructor(options?: MascotClockOptions) {
    const canvas = new Canvas({
      width: 440,
      height: 440,
      cache: true,
      draw: (ctx) => this.paint(ctx),
    });
    super({
      name: 'mascot-clock',
      pos: options?.pos === undefined ? vec(DESIGN_WIDTH / 2, 640) : options.pos,
      graphic: canvas,
    });
    this.bodyCanvas = canvas;
    const scale = options?.scale === undefined ? 1 : options.scale;
    this.scale = vec(scale, scale);

    const armGraphic = new Canvas({
      width: 84,
      height: 160,
      cache: true,
      draw: (ctx) => paintArm(ctx),
    });
    this.armCanvases.push(armGraphic);
    this.arm = new Actor({ name: 'mascot-arm', pos: vec(126, 58), graphic: armGraphic });
    this.arm.anchor = vec(0.5, 0.9);
    this.addChild(this.arm);

    const leftArmGraphic = new Canvas({
      width: 84,
      height: 160,
      cache: true,
      draw: (ctx) => paintArm(ctx),
    });
    this.armCanvases.push(leftArmGraphic);
    const leftArm = new Actor({ name: 'mascot-left-arm', pos: vec(-126, 58), graphic: leftArmGraphic });
    leftArm.anchor = vec(0.5, 0.9);
    leftArm.rotation = toRadians(-215);
    this.addChild(leftArm);
  }

  refreshTheme(): void {
    this.bodyCanvas.flagDirty();
    for (const canvas of this.armCanvases) {
      canvas.flagDirty();
    }
  }

  onPreUpdate(_engine: Engine, elapsed: number): void {
    const dt = elapsed / 1000;
    this.time += dt;
    this.arm.rotation = toRadians(30 + Math.sin(this.time * 2.9) * 22);
    this.rotation = toRadians(Math.sin(this.time * 0.9) * 1.4);
    this.blinkTimer -= dt;
    if (this.blinkTimer <= 0) {
      this.blinking = !this.blinking;
      this.blinkTimer = this.blinking ? 0.14 : 2.4 + Math.abs(Math.sin(this.time * 0.7)) * 2.4;
      this.bodyCanvas.flagDirty();
    }
    if (this.expressionTimer > 0) {
      this.expressionTimer -= dt;
      if (this.expressionTimer <= 0) {
        this.expression = 'idle';
        this.bodyCanvas.flagDirty();
      }
    }
    this.funnyTimer -= dt;
    if (this.funnyTimer <= 0) {
      this.funnyTimer = this.faceRng.floating(3.5, 7.5);
      if (this.expressionTimer <= 0) {
        this.expression = this.faceRng.pickOne(FUNNY_EXPRESSIONS);
        this.expressionTimer = 1.2;
        this.bodyCanvas.flagDirty();
      }
    }
  }

  private paint(ctx: CanvasRenderingContext2D): void {
    const rng = new Random(SEEDS.mascot);
    const mid = 220;
    paintClockBase(ctx, mid, mid, rng, {
      radius: 152,
      faceLight: Palette.cream,
      faceShade: Palette.paperDeep,
      rim: Palette.ink,
      tick: Palette.ink,
      accents: [Palette.mint, Palette.yellow, Palette.pink, Palette.blue],
    });
    drawFace(ctx, mid, mid - 4, 88, rng, this.expression, this.blinking);
  }
}

function paintArm(ctx: CanvasRenderingContext2D): void {
  const rng = new Random(SEEDS.mascot + 1);
  drawTaperedStroke(ctx, rng, 42, 146, 26, 15, Palette.blue, Palette.blueDeep);
}
